import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { Trash2, FolderPlus, Check, X, Loader2 } from 'lucide-react';
import { ConfirmDeleteModal } from '../controlPanel/ConfirmDeleteModal';
import { FolderTreePicker } from './FolderTreePicker';
export const HighlightSaveSection = ({ userFolders, onCreateFolder, onSaveToFolder, onSaveLater, onDiscard, confirmDiscard, isSavePending, itemDisplayName, defaultFolderColor, initialFolderId, hideSaveLater, }) => {
    const [saveFolderId, setSaveFolderId] = useState(initialFolderId !== null && initialFolderId !== void 0 ? initialFolderId : '');
    const [creatingFolder, setCreatingFolder] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [saveError, setSaveError] = useState(null);
    const [folderCreatePending, setFolderCreatePending] = useState(false);
    const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
    const newFolderInputRef = useRef(null);
    useEffect(() => {
        var _a;
        if (creatingFolder)
            (_a = newFolderInputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, [creatingFolder]);
    const trimmedNewFolderName = newFolderName.trim();
    const folderListEmpty = userFolders.length === 0;
    const busy = isSavePending || folderCreatePending;
    const canSave = !!saveFolderId && !busy;
    const handleSelectFolder = (folderId) => {
        setSaveFolderId(folderId);
        setCreatingFolder(false);
        setSaveError(null);
    };
    const handleCreateFolderInline = async () => {
        const desired = trimmedNewFolderName;
        if (desired.length === 0) {
            setSaveError('Folder name cannot be empty');
            return;
        }
        const dup = userFolders.some(f => f.name.toLowerCase() === desired.toLowerCase());
        if (dup) {
            setSaveError('A folder with that name already exists');
            return;
        }
        setSaveError(null);
        setFolderCreatePending(true);
        try {
            const folder = await onCreateFolder(desired, defaultFolderColor);
            setSaveFolderId(folder.id);
            setNewFolderName('');
            setCreatingFolder(false);
        }
        catch (err) {
            setSaveError(err instanceof Error ? err.message : 'Failed to create folder');
        }
        finally {
            setFolderCreatePending(false);
        }
    };
    const handleSave = async () => {
        if (!canSave)
            return;
        setSaveError(null);
        try {
            await onSaveToFolder(saveFolderId);
        }
        catch (err) {
            setSaveError(err instanceof Error ? err.message : 'Failed to save');
        }
    };
    const handleDiscardClick = () => {
        if (confirmDiscard) {
            setConfirmDiscardOpen(true);
        }
        else {
            onDiscard();
        }
    };
    const handleDiscardConfirm = () => {
        setConfirmDiscardOpen(false);
        onDiscard();
    };
    const handleDiscardCancel = () => setConfirmDiscardOpen(false);
    const handleSaveLater = async () => {
        setSaveError(null);
        try {
            await onSaveLater();
        }
        catch (err) {
            setSaveError(err instanceof Error ? err.message : 'Failed to save');
        }
    };
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "mt-3 pt-3 border-t", style: { borderColor: 'var(--pewter)' }, children: [_jsx("div", { className: "text-xs mb-1", style: { color: 'var(--dew)' }, children: "Save to folder" }), !creatingFolder && (_jsxs(_Fragment, { children: [_jsx(FolderTreePicker, { folders: userFolders.filter(f => !f.pending), selectedFolderId: saveFolderId, onSelect: handleSelectFolder, disabled: busy }), _jsxs("button", { onClick: () => { setCreatingFolder(true); setSaveError(null); }, disabled: busy, className: "mt-2 mb-1 flex items-center gap-1.5 px-2 py-1 rounded text-xs transition-colors", style: { color: 'var(--dew)', backgroundColor: 'transparent' }, onMouseEnter: (e) => { if (!busy)
                                    e.currentTarget.style.backgroundColor = 'var(--pewter)'; }, onMouseLeave: (e) => { e.currentTarget.style.backgroundColor = 'transparent'; }, title: folderListEmpty ? 'Create your first folder' : 'Create a new root folder', children: [_jsx(FolderPlus, { size: 14 }), _jsx("span", { children: folderListEmpty ? 'Create folder' : 'New folder' })] })] })), creatingFolder && (_jsxs("div", { className: "flex items-center gap-2 mb-2", children: [_jsx("input", { ref: newFolderInputRef, type: "text", value: newFolderName, onChange: (e) => { setNewFolderName(e.target.value); setSaveError(null); }, onKeyDown: (e) => {
                                    if (e.key === 'Enter')
                                        handleCreateFolderInline();
                                    if (e.key === 'Escape') {
                                        setCreatingFolder(false);
                                        setNewFolderName('');
                                        setSaveError(null);
                                    }
                                }, placeholder: "New folder name", className: "flex-1 px-2 py-1.5 rounded text-sm", style: {
                                    backgroundColor: 'var(--pewter)',
                                    color: 'var(--sand)',
                                    border: '1px solid var(--charcoal)',
                                }, disabled: busy }), _jsx("button", { onClick: handleCreateFolderInline, disabled: busy || trimmedNewFolderName.length === 0, className: "px-2 py-1.5 rounded text-xs", style: { backgroundColor: 'var(--fern)', color: '#FFFFFF', opacity: busy || trimmedNewFolderName.length === 0 ? 0.5 : 1 }, title: "Create folder", children: _jsx(Check, { size: 14 }) }), _jsx("button", { onClick: () => { setCreatingFolder(false); setNewFolderName(''); setSaveError(null); }, disabled: busy, className: "px-2 py-1.5 rounded text-xs", style: { backgroundColor: 'var(--pewter)', color: 'var(--sand)' }, title: "Cancel", children: _jsx(X, { size: 14 }) })] })), saveError && (_jsx("div", { className: "text-[11px] mb-2", style: { color: '#DC143C' }, children: saveError })), _jsx("button", { onClick: handleSave, disabled: !canSave, className: "w-full py-2 rounded-lg text-sm font-medium transition-colors mt-2", style: {
                            backgroundColor: canSave ? 'var(--fern)' : 'var(--pewter)',
                            color: '#FFFFFF',
                            opacity: canSave ? 1 : 0.6,
                            cursor: canSave ? 'pointer' : 'not-allowed',
                        }, title: !saveFolderId ? 'Choose a folder' :
                            isSavePending ? 'Saving…' : 'Save to folder', children: _jsxs("span", { className: "flex items-center justify-center gap-2", children: [isSavePending ? _jsx(Loader2, { size: 16, className: "animate-spin" }) : _jsx(FolderPlus, { size: 16 }), isSavePending ? 'Saving…' : 'Save'] }) }), _jsx("button", { onClick: handleDiscardClick, disabled: busy, className: "w-full py-2 rounded-lg text-sm font-medium transition-colors mt-2", style: { backgroundColor: '#DC143C', color: '#FFFFFF' }, onMouseEnter: (e) => { if (!busy)
                            e.currentTarget.style.backgroundColor = '#B91C1C'; }, onMouseLeave: (e) => { if (!busy)
                            e.currentTarget.style.backgroundColor = '#DC143C'; }, children: _jsxs("span", { className: "flex items-center justify-center gap-2", children: [_jsx(Trash2, { size: 16 }), "Discard"] }) }), !hideSaveLater && (_jsx("button", { onClick: handleSaveLater, disabled: busy, className: "w-full text-[11px] mt-2 underline-offset-2 hover:underline", style: { color: 'var(--dew)', background: 'none', border: 'none', padding: 0 }, children: "Save later (keep in Unsaved Drawings)" }))] }), _jsx(ConfirmDeleteModal, { isOpen: confirmDiscardOpen, itemName: itemDisplayName, onConfirm: handleDiscardConfirm, onCancel: handleDiscardCancel })] }));
};
export default HighlightSaveSection;
